import { useMemo, useState, type ReactNode } from 'react'
import { Card, EmptyState, Skeleton } from './primitives'

export type Sortare = { cheie: string; sens: 'asc' | 'desc' }

export type Coloana<T> = {
  cheie: string
  antet: string
  sortabil?: boolean
  aliniat?: 'dreapta'
  latime?: string
  /** cheia de sortare pe server, cand tabelul e paginat de server */
  cheieServer?: string
  valoare?: (r: T) => string | number | null
  randare: (r: T) => ReactNode
}

/** Tabelul paginat de server: lista nu incape in memorie (7.000 de facturi), deci
 *  filtrarea, sortarea si paginile le face baza de date, iar tabelul doar le cere. */
export type Server = {
  total: number
  pagina: number
  pePagina: number
  setPagina: (n: number) => void
  setPePagina: (n: number) => void
  termen: string
  setTermen: (s: string) => void
  sort?: Sortare
  setSort?: (s: Sortare) => void
}

const PE_PAGINA = [10, 25, 50, 100, 250]

/** Tabelul aplicatiei, in tiparul TravelJoy: antete sortabile, cautare, selector „pe pagina"
 *  si „X – Y din Z" cu Inapoi/Inainte. Toate listele il folosesc, ca sa se invete o data. */
export function DataTable<T>({
  randuri, coloane, cheie, incarca, cauta, placeholderCautare,
  peRand, gol, sortImplicit, actiuni, server,
}: {
  randuri: T[]
  coloane: Coloana<T>[]
  cheie: (r: T) => string | number
  incarca?: boolean
  cauta?: (r: T, termen: string) => boolean
  placeholderCautare?: string
  peRand?: (r: T) => void
  gol?: { titlu: string; indiciu?: string }
  sortImplicit?: Sortare
  actiuni?: ReactNode
  server?: Server
}) {
  const [termenLocal, setTermenLocal] = useState('')
  const [pePaginaLocal, setPePaginaLocal] = useState(25)
  const [paginaLocal, setPaginaLocal] = useState(0)
  const [sortLocal, setSortLocal] = useState<Sortare | null>(sortImplicit ?? null)

  const termen = server ? server.termen : termenLocal
  const setTermen = server ? server.setTermen : setTermenLocal
  const pePagina = server ? server.pePagina : pePaginaLocal
  const pagina = server ? server.pagina : paginaLocal
  const sort = server ? server.sort ?? null : sortLocal
  const cuCautare = Boolean(server || cauta)

  const filtrate = useMemo(() => {
    if (server) return randuri
    const t = termenLocal.trim().toLowerCase()
    let out = !t || !cauta ? randuri : randuri.filter((r) => cauta(r, t))
    if (sortLocal) {
      const col = coloane.find((c) => c.cheie === sortLocal.cheie)
      if (col?.valoare) {
        out = [...out].sort((a, b) => {
          const va = col.valoare!(a), vb = col.valoare!(b)
          if (va == null) return 1
          if (vb == null) return -1
          const cmp = typeof va === 'number' && typeof vb === 'number'
            ? va - vb : String(va).localeCompare(String(vb), 'ro')
          return sortLocal.sens === 'asc' ? cmp : -cmp
        })
      }
    }
    return out
  }, [randuri, termenLocal, sortLocal, cauta, coloane, server])

  const total = server ? server.total : filtrate.length
  const start = pagina * pePagina
  const vizibile = server ? randuri : filtrate.slice(start, start + pePagina)
  const pagini = Math.max(1, Math.ceil(total / pePagina))

  const seSorteaza = (c: Coloana<T>) =>
    Boolean(c.sortabil && (server ? server.setSort && c.cheieServer : c.valoare))

  const comuta = (c: Coloana<T>) => {
    if (!seSorteaza(c)) return
    const sens: 'asc' | 'desc' = sort?.cheie === c.cheie && sort.sens === 'asc' ? 'desc' : 'asc'
    const noua: Sortare = { cheie: c.cheie, sens: sort?.cheie === c.cheie ? sens : 'asc' }
    if (server) { server.setSort!(noua); server.setPagina(0) } else { setSortLocal(noua); setPaginaLocal(0) }
  }

  const setPagina = (n: number) => (server ? server.setPagina(n) : setPaginaLocal(n))
  const setPePagina = (n: number) => {
    if (server) { server.setPePagina(n); server.setPagina(0) } else { setPePaginaLocal(n); setPaginaLocal(0) }
  }

  return (
    <div className="space-y-3">
      {(cuCautare || actiuni) && (
        <div className="flex flex-wrap items-center gap-3">
          {cuCautare && (
            <input
              value={termen}
              onChange={(e) => { setTermen(e.target.value); setPagina(0) }}
              placeholder={placeholderCautare ?? 'Cauta…'}
              aria-label="Cauta in tabel"
              className="h-9 w-72 px-3 rounded-[var(--radius-control)] border border-line bg-surface text-[14px]"
            />
          )}
          {actiuni && <div className="ml-auto flex items-center gap-2">{actiuni}</div>}
        </div>
      )}

      <Card className="overflow-hidden">
        {incarca ? <Skeleton /> : total === 0 ? (
          <EmptyState title={gol?.titlu ?? 'Nimic aici'} hint={gol?.indiciu} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="text-left text-[11.5px] uppercase tracking-[0.1em] text-muted">
                  {coloane.map((c) => {
                    const activ = sort?.cheie === c.cheie
                    return (
                      <th key={c.cheie} style={c.latime ? { width: c.latime } : undefined}
                        className={'font-medium px-5 py-3 ' + (c.aliniat === 'dreapta' ? 'text-right' : '')}>
                        {seSorteaza(c) ? (
                          <button onClick={() => comuta(c)}
                            className="inline-flex items-center gap-1 hover:text-ink transition-colors"
                            title={`Sorteaza dupa ${c.antet}`}>
                            {c.antet}
                            <span className={activ ? 'text-accent' : 'text-line-strong'}>
                              {activ && sort!.sens === 'desc' ? '▾' : '▴'}
                            </span>
                          </button>
                        ) : c.antet}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {vizibile.map((r) => (
                  <tr key={cheie(r)}
                    onClick={peRand ? () => peRand(r) : undefined}
                    className={'border-t border-line ' + (peRand ? 'cursor-pointer hover:bg-page/70' : '')}>
                    {coloane.map((c) => (
                      <td key={c.cheie} className={'px-5 py-3 ' + (c.aliniat === 'dreapta' ? 'text-right' : '')}>
                        {c.randare(r)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > 0 && (
        <div className="flex flex-wrap items-center gap-4 text-[13px] text-muted">
          <label className="flex items-center gap-2">
            Pe pagina:
            <select value={pePagina} onChange={(e) => setPePagina(Number(e.target.value))}
              className="h-8 px-2 rounded-[var(--radius-control)] border border-line bg-surface text-[13px]">
              {PE_PAGINA.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <span className="tnum">
            {start + 1} – {Math.min(start + pePagina, total)} din {total.toLocaleString('ro-RO')}
          </span>
          <div className="ml-auto flex gap-2">
            <button onClick={() => setPagina(Math.max(0, pagina - 1))} disabled={pagina === 0}
              className="h-8 px-3 rounded-[var(--radius-control)] border border-line-strong disabled:opacity-40">Inapoi</button>
            <button onClick={() => setPagina(pagina + 1)} disabled={pagina + 1 >= pagini}
              className="h-8 px-3 rounded-[var(--radius-control)] border border-line-strong disabled:opacity-40">Inainte</button>
          </div>
        </div>
      )}
    </div>
  )
}
